import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import tradingService from '../services/tradingService';
import {isCanceledRequest} from '../services/api';
import './StrategyDetailsPage.css';
import ModalShell from '../components/ModalShell';
import AccountPerformanceChart from '../components/AccountPerformanceChart';
import {isTruthy} from '../utils/boolean';

const formatNumber = (value, options = {}) => {
  if (value === null || value === undefined || value === '') return '—';

  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);

  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 6,
    ...options,
  }).format(num);
};

const formatDateTime = (value) => {
  if (!value) return '—';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const getNestedLabel = (obj, keys, fallback = '—') => {
  for (const key of keys) {
    const value = obj?.[key];
    if (value !== null && value !== undefined && value !== '') return value;
  }
  return fallback;
};

export default function StrategyDetailsPage() {
  const { strategyId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [strategy, setStrategy] = useState(null);

  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);

  const [accountNumber, setAccountNumber] = useState('');
  const [volumeLimit, setVolumeLimit] = useState('0.9');

  const [submitting, setSubmitting] = useState(false);
  const [modalError, setModalError] = useState('');
  const [modalOk, setModalOk] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const [visibilityError, setVisibilityError] = useState('');
  const [visibilityOk, setVisibilityOk] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editing, setEditing] = useState(false);
  const [editError, setEditError] = useState('');

  useEffect(() => {
    if (!strategyId) return;

    const ac = new AbortController();

    (async () => {
      try {
        setLoading(true);
        setError('');
        setVisibilityError('');
        setVisibilityOk('');

        const res = await tradingService.strategies.get({
          strategyId,
          recentTradesLimit: 10,
          signal: ac.signal,
        });

        const payload = res?.data?.data ?? res?.data ?? null;
        setStrategy(payload?.strategies?.[0] ?? null);
      } catch (err) {
        if (isCanceledRequest(err)) {
          return;
        }

        setStrategy(null);
        setError(err?.message || 'Failed to load strategy details');
      } finally {
        if (!ac.signal.aborted) {
          setLoading(false);
        }
      }
    })();

    return () => ac.abort();
  }, [strategyId]);

  const nameText = strategy?.name ?? '—';
  const descText = strategy?.description ?? '—';

  const isActive = isTruthy(strategy?.is_active);
  const activeText = isActive ? 'Active' : 'Inactive';
  const isPublic = isTruthy(strategy?.is_public);
  const publicVisibilityLocked = isTruthy(strategy?.public_visibility_locked);
  const isOwner = isTruthy(strategy?.is_owner);

  const performance = strategy?.performance ?? {};
  const subscriptions = strategy?.subscriptions ?? {};
  const account = strategy?.account ?? {};
  const activity = strategy?.activity ?? {};
  const recentTrades = Array.isArray(strategy?.recent_trades) ? strategy.recent_trades : [];
  const userSubscription = subscriptions?.user_subscription ?? null;
  const hasActiveUserSubscription = isTruthy(userSubscription?.is_active);

  const accountOptions = useMemo(() => {
    return (Array.isArray(accounts) ? accounts : [])
      .map((a) => {
        const number = String(a?.number ?? '').trim();
        if (!number) return null;
        const label = String(a?.label ?? '').trim();
        return {
          value: number,
          label: label || 'Account',
        };
      })
      .filter(Boolean);
  }, [accounts]);

  const openSubscribe = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!isActive || hasActiveUserSubscription) return;
    setSubscribeOpen(true);
  };

  const closeSubscribe = () => {
    if (submitting) return;
    setSubscribeOpen(false);
    setModalError('');
    setModalOk('');
  };

  const setVisibility = async (nextIsPublic) => {
    if (!strategy?.short_id || !isOwner) return;

    setPublishing(true);
    setVisibilityError('');
    setVisibilityOk('');

    try {
      await tradingService.strategies.upsert({
        trading_strategy_id: strategy.short_id,
        payload: {
          name: strategy?.name ?? strategy?.label ?? 'New strategy',
          ...(strategy?.description ? { description: strategy.description } : null),
          is_public: nextIsPublic,
        },
      });

      const refreshed = await tradingService.strategies.get({
        strategyId,
        recentTradesLimit: 10,
      });
      const payload = refreshed?.data?.data ?? refreshed?.data ?? null;
      setStrategy(payload?.strategies?.[0] ?? strategy);
      setVisibilityOk(
        nextIsPublic
          ? 'Strategy is now public. Public strategies cannot be made private again.'
          : 'Strategy visibility updated'
      );
    } catch (err) {
      setVisibilityError(err?.message || 'Failed to update strategy visibility');
    } finally {
      setPublishing(false);
    }
  };

  const requestMakePublic = () => {
    if (!strategy?.short_id || !isOwner || isPublic) return;
    setVisibilityError('');
    setVisibilityOk('');
    setPublishConfirmOpen(true);
  };

  const openEdit = () => {
    if (!isOwner || !strategy?.short_id) return;
    setEditName(String(strategy?.name ?? strategy?.label ?? ''));
    setEditDescription(String(strategy?.description ?? ''));
    setEditError('');
    setEditOpen(true);
  };

  const closeEdit = () => {
    if (editing) return;
    setEditOpen(false);
    setEditError('');
  };

  const submitEdit = async (e) => {
    e.preventDefault();

    if (!isOwner || !strategy?.short_id) return;

    const name = editName.trim();
    if (!name) {
      setEditError('Strategy name is required');
      return;
    }

    setEditing(true);
    setEditError('');

    try {
      await tradingService.strategies.upsert({
        trading_strategy_id: strategy.short_id,
        payload: {
          name,
          description: editDescription.trim(),
        },
      });

      const refreshed = await tradingService.strategies.get({
        strategyId,
        recentTradesLimit: 10,
      });
      const payload = refreshed?.data?.data ?? refreshed?.data ?? null;
      setStrategy(payload?.strategies?.[0] ?? strategy);
      setEditOpen(false);
    } catch (err) {
      setEditError(err?.message || 'Failed to update strategy details');
    } finally {
      setEditing(false);
    }
  };

  useEffect(() => {
    if (!subscribeOpen) return;

    const ac = new AbortController();

    (async () => {
      try {
        setAccountsLoading(true);

        const res = await tradingService.accounts.list({
          limit: 200,
          offset: 0,
          signal: ac.signal,
        });

        setAccounts(res?.data?.accounts ?? []);
      } catch (err) {
        if (isCanceledRequest(err)) {
          return;
        }

        setAccounts([]);
      } finally {
        if (!ac.signal.aborted) {
          setAccountsLoading(false);
        }
      }
    })();

    return () => ac.abort();
  }, [subscribeOpen]);

  const submitSubscribe = async () => {
    setModalError('');
    setModalOk('');

    if (!isActive) {
      setModalError('Strategy is not active');
      return;
    }

    if (hasActiveUserSubscription) {
      setModalError('You are already subscribed to this strategy');
      return;
    }

    if (!strategy?.short_id) {
      setModalError('Strategy short_id is missing');
      return;
    }
    if (!accountNumber) {
      setModalError('Please select an account');
      return;
    }

    const vol = Number(volumeLimit);
    if (!Number.isFinite(vol) || vol <= 0) {
      setModalError('Volume limit must be a number greater than 0');
      return;
    }

    try {
      setSubmitting(true);

      await tradingService.accounts.strategy.subscribe({
        number: accountNumber,
        strategyShortId: strategy.short_id,
        volume_limit: vol,
      });

      setModalOk('Subscription created');

      const refreshed = await tradingService.strategies.get({
        strategyId,
        recentTradesLimit: 10,
      });
      const payload = refreshed?.data?.data ?? refreshed?.data ?? null;
      setStrategy(payload?.strategies?.[0] ?? strategy);

      setTimeout(() => {
        setSubscribeOpen(false);
        setSubmitting(false);
      }, 600);
    } catch (err) {
      setModalError(err?.message || 'Failed to create subscription');
      setSubmitting(false);
    }
  };

  return (
    <div className="card strategy-details">
      <div className="strategy-details__header">
        <div>
          <h1 className="strategy-details__title">{!loading && strategy ? nameText : 'Strategy'}</h1>
        </div>

        <div className="strategy-details__actions">
          {!loading && strategy && isOwner ? (
            <button
              type="button"
              className="strategy-details__btn"
              onClick={openEdit}
              disabled={editing}
              title="Edit strategy details"
            >
              Edit details
            </button>
          ) : null}

          {!loading && strategy && isOwner && !isPublic ? (
            <button
              type="button"
              className="strategy-details__btn strategy-details__btn--primary"
              onClick={requestMakePublic}
              disabled={publishing}
              title="Make this strategy public"
            >
              Make public
            </button>
          ) : null}

          {!loading && strategy && isActive ? (
            <button
              type="button"
              className="strategy-details__btn strategy-details__btn--primary"
              onClick={openSubscribe}
              disabled={!strategy?.short_id || hasActiveUserSubscription}
              title={
                hasActiveUserSubscription
                  ? 'You are already subscribed'
                  : !strategy?.short_id
                    ? 'Strategy short_id is missing'
                    : 'Create subscription'
              }
            >
              {hasActiveUserSubscription ? 'Already subscribed' : 'Subscribe'}
            </button>
          ) : null}

          <button type="button" className="strategy-details__btn" onClick={() => navigate('/my/strategies')}>
            Back
          </button>
        </div>
      </div>

      {error ? <p className="strategy-details__error">{error}</p> : null}
      {loading ? <p>Loading…</p> : null}

      {!loading && !strategy && !error ? (
        <div className="strategy-details__empty">Strategy not found.</div>
      ) : null}

      {!loading && strategy ? (
        <>
          <section className="strategy-details__hero">
            <div className="strategy-details__hero-main">
              <div className="strategy-details__kicker">Overview</div>

              <div className="strategy-details__title-row">
                <div className="strategy-details__name" title={nameText}>
                  {nameText}
                </div>

                <div className="strategy-details__badges">
                  <span
                    className={
                      isActive
                        ? 'strategy-details__badge strategy-details__badge--active'
                        : 'strategy-details__badge strategy-details__badge--inactive'
                    }
                  >
                    {activeText}
                  </span>
                  <span
                    className={
                      isPublic
                        ? 'strategy-details__badge strategy-details__badge--public'
                        : 'strategy-details__badge strategy-details__badge--private'
                    }
                  >
                    {isPublic ? 'Public' : 'Private'}
                  </span>
                </div>
              </div>

              <p className="strategy-details__description">{descText}</p>

            </div>

            <div className="strategy-details__hero-side">
              <div className="strategy-details__mini-card">
                <span>Exchange</span>
                <strong>{account?.exchange?.exchange_name ?? '—'}</strong>
              </div>
            </div>
          </section>

          {account?.number ? <AccountPerformanceChart number={account.number} /> : null}

          {visibilityError ? <div className="strategy-details__error">{visibilityError}</div> : null}
          {visibilityOk ? <div className="strategy-details__success">{visibilityOk}</div> : null}
          {!loading && strategy && isOwner && publicVisibilityLocked ? (
            <div className="strategy-details__visibility-note">
              This strategy is public and cannot be switched back to private.
            </div>
          ) : null}

          <section className="strategy-details__section">
            <div className="strategy-details__section-head">
              <div>
                <div className="strategy-details__kicker">Performance</div>
                <h2 className="strategy-details__section-title">Trading activity</h2>
              </div>
              <span className="strategy-details__section-note">Based on recorded strategy orders</span>
            </div>

            <div className="strategy-details__stats-grid">
              <div className="strategy-details__stat">
                <span>Total trades</span>
                <strong>{formatNumber(performance?.total_trades)}</strong>
              </div>
              <div className="strategy-details__stat">
                <span>Executed trades</span>
                <strong>{formatNumber(performance?.executed_trades)}</strong>
              </div>
              <div className="strategy-details__stat">
                <span>Pending / open</span>
                <strong>{formatNumber(performance?.open_or_pending_trades)}</strong>
              </div>
              <div className="strategy-details__stat">
                <span>Total executed notional</span>
                <strong>{formatNumber(performance?.total_executed_notional)}</strong>
              </div>
              <div className="strategy-details__stat">
                <span>Avg executed notional</span>
                <strong>{formatNumber(performance?.avg_executed_notional)}</strong>
              </div>
              <div className="strategy-details__stat">
                  <span>Net return (USDT)</span>
                  <strong>{formatNumber(performance?.total_return_usdt)}</strong>
              </div>
                <div className="strategy-details__stat">
                    <span>Fees (USDT)</span>
                    <strong>{formatNumber(performance?.total_fees)}</strong>
                </div>
                <div className="strategy-details__stat">
                    <span>Open P/L (USDT)</span>
                    <strong>{formatNumber(performance?.unrealized_return_usdt)}</strong>
                </div>
                <div className="strategy-details__stat">
                <span>Last trade</span>
                <strong>{formatDateTime(performance?.last_trade_date)}</strong>
              </div>
            </div>
          </section>

          <section className="strategy-details__section">
            <div className="strategy-details__section-head">
              <div>
                <div className="strategy-details__kicker">Subscriptions</div>
                <h2 className="strategy-details__section-title">Follower activity</h2>
              </div>
            </div>

            <div className="strategy-details__grid">
              <div className="strategy-details__kv">
                <div className="strategy-details__kv-k">Active subscribers</div>
                <div className="strategy-details__kv-v">{formatNumber(subscriptions?.active)}</div>
              </div>

              <div className="strategy-details__kv">
                <div className="strategy-details__kv-k">Total subscriptions</div>
                <div className="strategy-details__kv-v">{formatNumber(subscriptions?.total)}</div>
              </div>

              <div className="strategy-details__kv">
                <div className="strategy-details__kv-k">Your status</div>
                <div className="strategy-details__kv-v">
                  {hasActiveUserSubscription ? 'Subscribed' : 'Not subscribed'}
                </div>
              </div>
            </div>
          </section>

          <section className="strategy-details__section">
            <div className="strategy-details__section-head">
              <div>
                <div className="strategy-details__kicker">Trades</div>
                <h2 className="strategy-details__section-title">Recent trades</h2>
              </div>
            </div>

            {recentTrades.length ? (
              <div className="strategy-details__table-wrap">
                <table className="strategy-details__table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Symbol</th>
                      <th>Direction</th>
                      <th>Status</th>
                      <th>Qty</th>
                      <th>Quote qty</th>
                      <th>Exec price</th>
                      <th>Notional</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentTrades.map((trade) => {
                      const tradeId = trade?.trade_order_id ?? trade?.id;
                      const symbol = getNestedLabel(trade?.symbol, ['symbol', 'name', 'label']);
                      const direction = getNestedLabel(trade?.direction, ['name', 'label']);
                      const status = getNestedLabel(trade?.trade_status, ['label', 'name']);

                      return (
                        <tr key={tradeId}>
                          <td>{formatDateTime(trade?.date_created)}</td>
                          <td>{symbol}</td>
                          <td>{direction}</td>
                          <td>{status}</td>
                          <td>{formatNumber(trade?.quantity ?? trade?.executed_quantity)}</td>
                          <td>{formatNumber(trade?.quote_quantity)}</td>
                          <td>{formatNumber(trade?.executed_price)}</td>
                          <td>{formatNumber(trade?.notional)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="strategy-details__empty">No trades recorded for this strategy yet.</div>
            )}
          </section>

          <section className="strategy-details__section">
            <div className="strategy-details__section-head">
              <div>
                <div className="strategy-details__kicker">Activity</div>
                <h2 className="strategy-details__section-title">Timeline</h2>
              </div>
            </div>

            <div className="strategy-details__grid">
              <div className="strategy-details__kv">
                <div className="strategy-details__kv-k">Created</div>
                <div className="strategy-details__kv-v">{formatDateTime(activity?.date_created)}</div>
              </div>

              <div className="strategy-details__kv">
                <div className="strategy-details__kv-k">Updated</div>
                <div className="strategy-details__kv-v">{formatDateTime(activity?.date_updated)}</div>
              </div>

              <div className="strategy-details__kv">
                <div className="strategy-details__kv-k">Last trade</div>
                <div className="strategy-details__kv-v">{formatDateTime(activity?.last_trade_date)}</div>
              </div>
            </div>
          </section>

          <ModalShell
            title="Edit strategy details"
            open={editOpen}
            onClose={closeEdit}
            footer={
              <>
                <button type="button" onClick={closeEdit} disabled={editing}>
                  Cancel
                </button>
                <button
                  type="submit"
                  form="edit-strategy-form"
                  className="strategy-details__btn strategy-details__btn--primary"
                  disabled={editing || !editName.trim()}
                >
                  {editing ? 'Saving…' : 'Save changes'}
                </button>
              </>
            }
          >
            <form id="edit-strategy-form" onSubmit={submitEdit} className="strategy-details__form-grid">
              {editError ? <div className="strategy-details__modal-error">{editError}</div> : null}

              <label>
                <div className="strategy-details__field-title">Strategy name</div>
                <input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  disabled={editing}
                  maxLength={50}
                  autoComplete="off"
                  className="strategy-details__control"
                  required
                />
              </label>

              <label>
                <div className="strategy-details__field-title">Description</div>
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  disabled={editing}
                  className="strategy-details__control"
                  rows={5}
                />
              </label>
            </form>
          </ModalShell>

          <ModalShell
            title="Make strategy public"
            open={publishConfirmOpen}
            onClose={() => {
              if (publishing) return;
              setPublishConfirmOpen(false);
            }}
            footer={
              <>
                <button type="button" onClick={() => setPublishConfirmOpen(false)} disabled={publishing}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="strategy-details__btn strategy-details__btn--primary"
                  onClick={async () => {
                    setPublishConfirmOpen(false);
                    await setVisibility(true);
                  }}
                  disabled={publishing}
                >
                  {publishing ? 'Publishing…' : 'Make public'}
                </button>
              </>
            }
          >
            <div className="strategy-details__publish-note">
              This will make the strategy visible in the screener to other authenticated users.
            </div>
            <div className="strategy-details__publish-note strategy-details__publish-note--warning">
              Public visibility is permanent. You will not be able to change this strategy back to private.
            </div>
          </ModalShell>

          <ModalShell
            title={`Subscribe to ${nameText}`}
            open={subscribeOpen}
            onClose={closeSubscribe}
            footer={
              <>
                <button type="button" onClick={closeSubscribe} disabled={submitting}>
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submitSubscribe}
                  disabled={submitting || accountsLoading || !accountNumber}
                >
                  {submitting ? 'Creating…' : 'Create subscription'}
                </button>
              </>
            }
          >
            {modalError ? <div className="strategy-details__modal-error">{modalError}</div> : null}
            {modalOk ? <div className="strategy-details__modal-ok">{modalOk}</div> : null}

            <div className="strategy-details__form-grid">
              <label>
                <div className="strategy-details__field-title">Account</div>
                <select
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                  disabled={accountsLoading || submitting}
                  className="strategy-details__control"
                >
                  <option value="" disabled>
                    {accountsLoading ? 'Loading accounts…' : 'Select account…'}
                  </option>
                  {accountOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                <div className="strategy-details__field-title">Volume limit</div>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  value={volumeLimit}
                  onChange={(e) => setVolumeLimit(e.target.value)}
                  disabled={submitting}
                  className="strategy-details__control"
                />
              </label>

            </div>
          </ModalShell>
        </>
      ) : null}
    </div>
  );
}
